import axios from 'axios';
import { createAsyncThunk, isFulfilled, isPending } from '@reduxjs/toolkit';

import { cleanEntity } from 'app/shared/util/entity-utils';
import { IQueryParams, createEntitySlice, EntityState, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { IVirtualFenceCircle, defaultValue } from 'app/shared/model/virtual-fence-circle.model';
import { markPendingDelete } from 'app/entities/virtual-fence/virtual-fence-list.reducer';

const initialState: EntityState<IVirtualFenceCircle> = {
  loading: false,
  loadingList: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

const apiUrl = 'api/virtual-fence-circles';

type VirtualFenceCircleQueryParams = IQueryParams & { companyId?: number };

const buildRequestUrl = ({ page, size, sort, companyId }: VirtualFenceCircleQueryParams = {}) => {
  const params = new URLSearchParams();
  if (page !== undefined) params.append('page', `${page}`);
  if (size !== undefined) params.append('size', `${size}`);
  if (sort) params.append('sort', sort);
  if (companyId !== undefined) params.append('companyId', `${companyId}`);
  params.append('cacheBuster', `${new Date().getTime()}`);
  return `${apiUrl}?${params.toString()}`;
};

export const getEntities = createAsyncThunk('virtualFenceCircle/fetch_entity_list', async (params: VirtualFenceCircleQueryParams = {}) => {
  const requestUrl = buildRequestUrl(params);
  return axios.get<IVirtualFenceCircle[]>(requestUrl);
});

export const pollFences = createAsyncThunk('virtualFenceCircle/poll_entity_list', async (params: VirtualFenceCircleQueryParams = {}) => {
  const requestUrl = buildRequestUrl(params);
  return axios.get<IVirtualFenceCircle[]>(requestUrl);
});

export const getEntity = createAsyncThunk(
  'virtualFenceCircle/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<IVirtualFenceCircle>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const createEntity = createAsyncThunk(
  'virtualFenceCircle/create_entity',
  async (entity: IVirtualFenceCircle, thunkAPI) => {
    const result = await axios.post<IVirtualFenceCircle>(apiUrl, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateEntity = createAsyncThunk(
  'virtualFenceCircle/update_entity',
  async (entity: IVirtualFenceCircle, thunkAPI) => {
    const result = await axios.put<IVirtualFenceCircle>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const partialUpdateEntity = createAsyncThunk(
  'virtualFenceCircle/partial_update_entity',
  async (entity: IVirtualFenceCircle, thunkAPI) => {
    const result = await axios.patch<IVirtualFenceCircle>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteEntity = createAsyncThunk(
  'virtualFenceCircle/delete_entity',
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    await axios.delete<IVirtualFenceCircle>(requestUrl);
    const numericId = Number(id);
    thunkAPI.dispatch(markPendingDelete({ type: 'CIRCLE', id: numericId }));
    return numericId;
  },
  { serializeError: serializeAxiosError }
);

export const VirtualFenceCircleSlice = createEntitySlice({
  name: 'virtualFenceCircle',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, (state, action) => {
        const deletedId = action.payload;
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
        state.entities = state.entities.map(fence => (fence.id === deletedId ? { ...fence, syncStatus: 'PENDING_DELETE' as const } : fence));
      })
      .addCase(pollFences.pending, state => {
        state.loadingList = true;
      })
      .addCase(pollFences.fulfilled, (state, action) => {
        state.loadingList = false;
        state.entities = action.payload.data;
        state.totalItems = parseInt(action.payload.headers['x-total-count'], 10);
      })
      .addCase(pollFences.rejected, state => {
        state.loadingList = false;
      })
      .addMatcher(isFulfilled(getEntities), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(createEntity, updateEntity, partialUpdateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getEntities, getEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createEntity, updateEntity, partialUpdateEntity, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = VirtualFenceCircleSlice.actions;

export default VirtualFenceCircleSlice.reducer;
